import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import Popup from "../../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../../components/styles";
import {
    getExpenseById,
    getAllCategories,
    updateExpense,
    approveExpense,
    rejectExpense,
} from "../../../redux/expenseRelated/expenseHandle";
import { underExpenseControl } from "../../../redux/expenseRelated/expenseSlice";
import { getSchoolIdForUser } from "../../../utils/userContext";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const PAYMENT_METHODS = ["Cash", "Airtel Money", "TNM Mpamba", "Bank Transfer", "Cheque", "Other"];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();
const formatDate = (d) => new Date(d).toLocaleDateString("en-GB");

function statusChipColor(status) {
    const s = String(status || "");
    if (s === "Pending") return "warning";
    if (s === "Approved") return "success";
    if (s === "Rejected") return "error";
    return "default";
}

function toDateInputValue(d) {
    if (!d) return "";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return "";
    return x.toISOString().slice(0, 10);
}

function isImageAttachmentUrl(url) {
    if (!url || typeof url !== "string") return false;
    const path = url.split("?")[0].toLowerCase();
    return /\.(jpe?g|png|webp)$/i.test(path);
}

function DetailTableRows({ rows }) {
    return (
        <Table size="small">
            <TableBody>
                {rows.map(([label, value]) => (
                    <StyledTableRow key={label}>
                        <StyledTableCell sx={{ border: 0, verticalAlign: "top", width: "42%", py: 1.25 }}>
                            <Typography variant="caption" color="text.secondary">
                                {label}
                            </Typography>
                        </StyledTableCell>
                        <StyledTableCell sx={{ border: 0, py: 1.25 }}>
                            <Typography variant="body2" fontWeight={500}>
                                {value !== undefined && value !== null && String(value).trim() !== "" ? value : "—"}
                            </Typography>
                        </StyledTableCell>
                    </StyledTableRow>
                ))}
            </TableBody>
        </Table>
    );
}

const ExpenseDetail = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser } = useSelector((s) => s.user);
    const { selectedExpense, loading, statestatus, categoriesList } = useSelector((s) => s.expense);

    const schoolId = getSchoolIdForUser(currentUser);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [approveOpen, setApproveOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);

    const y = new Date().getFullYear();
    const ACADEMIC_YEARS = useMemo(() => [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`], [y]);

    const idMatch =
        selectedExpense && String(selectedExpense._id || selectedExpense.id) === String(id);
    const expense = idMatch ? selectedExpense : null;

    const showLoader = loading && !idMatch;
    const showNotFound = !loading && !idMatch;

    useEffect(() => {
        if (id) {
            dispatch(getExpenseById(id));
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (statestatus !== "added" || !id) return;
        dispatch(getExpenseById(id));
        setApproveOpen(false);
        setRejectOpen(false);
        setRejectReason("");
        setEditOpen(false);
        setEditForm(null);
        setMessage("Action completed successfully.");
        setShowPopup(true);
        dispatch(underExpenseControl({ preserveLoading: true }));
    }, [statestatus, id, dispatch]);

    const openEdit = useCallback(() => {
        if (!expense || expense.status !== "Pending") return;
        if (schoolId && (!categoriesList || categoriesList.length === 0)) {
            dispatch(getAllCategories(schoolId));
        }
        setEditForm({
            title: expense.title ?? "",
            amount: Number(expense.amount) || 0,
            categoryId: String(expense.categoryId || expense.category?._id || expense.category?.id || ""),
            description: expense.description ?? "",
            payee: expense.payee ?? "",
            receiptRef: expense.receiptRef ?? "",
            referenceNumber: expense.referenceNumber ?? "",
            paymentMethod: expense.paymentMethod || "Cash",
            expenseDate: toDateInputValue(expense.expenseDate),
            term: expense.term || "Term 1",
            academicYear: expense.academicYear || ACADEMIC_YEARS[1],
            notes: expense.notes ?? "",
        });
        setEditOpen(true);
    }, [expense, schoolId, categoriesList, dispatch, ACADEMIC_YEARS]);

    const buildChangedFields = useCallback(() => {
        if (!expense || !editForm) return {};
        const cur = {
            title: String(expense.title ?? "").trim(),
            amount: Number(expense.amount) || 0,
            categoryId: String(expense.categoryId || expense.category?._id || expense.category?.id || ""),
            description: String(expense.description ?? "").trim(),
            payee: String(expense.payee ?? "").trim(),
            receiptRef: String(expense.receiptRef ?? "").trim(),
            referenceNumber: String(expense.referenceNumber ?? "").trim(),
            paymentMethod: String(expense.paymentMethod || "Cash"),
            expenseDate: toDateInputValue(expense.expenseDate),
            term: String(expense.term || "").trim(),
            academicYear: String(expense.academicYear || "").trim(),
            notes: String(expense.notes ?? "").trim(),
        };
        const next = {
            title: String(editForm.title ?? "").trim(),
            amount: Number(editForm.amount === "" ? NaN : editForm.amount) || 0,
            categoryId: String(editForm.categoryId ?? "").trim(),
            description: String(editForm.description ?? "").trim(),
            payee: String(editForm.payee ?? "").trim(),
            receiptRef: String(editForm.receiptRef ?? "").trim(),
            referenceNumber: String(editForm.referenceNumber ?? "").trim(),
            paymentMethod: String(editForm.paymentMethod || "Cash"),
            expenseDate: String(editForm.expenseDate ?? "").trim(),
            term: String(editForm.term ?? "").trim(),
            academicYear: String(editForm.academicYear ?? "").trim(),
            notes: String(editForm.notes ?? "").trim(),
        };
        const keys = Object.keys(next);
        const changed = {};
        for (const k of keys) {
            if (cur[k] !== next[k]) {
                changed[k] = k === "amount" ? next[k] : next[k];
            }
        }
        return changed;
    }, [expense, editForm]);

    const handleSaveEdit = () => {
        if (!id || !editForm) return;
        const changed = buildChangedFields();
        if (Object.keys(changed).length === 0) {
            setEditOpen(false);
            return;
        }
        dispatch(updateExpense(id, changed));
    };

    const leftRows = expense
        ? [
              ["Title", expense.title],
              ["Category", expense.category?.name],
              ["Description", expense.description],
              ["Payee", expense.payee],
              ["Receipt/Invoice Ref", expense.receiptRef],
              ["Reference Number", expense.referenceNumber],
          ]
        : [];

    const approvedByLabel =
        expense?.approvedBy?.name ||
        (expense?.approvedById ? `ID: ${expense.approvedById}` : null);

    const rightRows = expense
        ? [
              ["Amount", formatMWK(expense.amount)],
              ["Payment Method", expense.paymentMethod],
              ["Expense Date", expense.expenseDate ? formatDate(expense.expenseDate) : "—"],
              ["Term", expense.term],
              ["Academic Year", expense.academicYear],
              ["Status", expense.status],
              ["Approved By", approvedByLabel],
              ["Approved At", expense.approvedAt ? formatDate(expense.approvedAt) : null],
          ]
        : [];

    const attachmentUrl = expense?.attachmentUrl?.trim?.() ? String(expense.attachmentUrl).trim() : "";

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            {showLoader && (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            )}

            {showNotFound && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Expense not found
                </Alert>
            )}

            {expense && !showLoader && (
                <>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <IconButton aria-label="back" onClick={() => navigate(-1)} edge="start" size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h5" component="h1" fontWeight={600}>
                                {expense.title}
                            </Typography>
                            <Chip label={expense.status || "—"} color={statusChipColor(expense.status)} size="small" sx={{ height: 28 }} />
                        </Stack>

                        {expense.status === "Pending" && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setApproveOpen(true)}
                                >
                                    Approve
                                </Button>
                                <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => setRejectOpen(true)}>
                                    Reject
                                </Button>
                                <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
                                    Edit
                                </Button>
                            </Stack>
                        )}
                    </Stack>

                    {expense.status === "Rejected" && expense.rejectedReason?.trim() ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {expense.rejectedReason.trim()}
                        </Alert>
                    ) : null}

                    <Paper variant="outlined" sx={{ p: 3, mb: 3, textAlign: "center" }}>
                        <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5 }}>
                            {formatMWK(expense.amount)}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                            {expense.category?.name ? <Chip label={expense.category.name} size="small" variant="outlined" /> : null}
                            {expense.paymentMethod ? <Chip label={expense.paymentMethod} size="small" variant="outlined" /> : null}
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                {expense.expenseDate ? formatDate(expense.expenseDate) : "—"}
                            </Typography>
                            {expense.term ? (
                                <>
                                    <Typography variant="body2" color="text.disabled">
                                        ·
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {expense.term}
                                    </Typography>
                                </>
                            ) : null}
                            {expense.academicYear ? (
                                <>
                                    <Typography variant="body2" color="text.disabled">
                                        ·
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {expense.academicYear}
                                    </Typography>
                                </>
                            ) : null}
                        </Stack>
                    </Paper>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                    Expense Information
                                </Typography>
                                <TableContainer>
                                    <DetailTableRows rows={leftRows} />
                                </TableContainer>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                    Payment & Status
                                </Typography>
                                <TableContainer>
                                    <DetailTableRows rows={rightRows} />
                                </TableContainer>
                            </Paper>
                        </Grid>
                    </Grid>

                    {expense.notes?.trim() ? (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                Notes
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {expense.notes.trim()}
                            </Typography>
                        </Paper>
                    ) : null}

                    {attachmentUrl ? (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                Receipt / Attachment
                            </Typography>
                            {isImageAttachmentUrl(attachmentUrl) ? (
                                <img
                                    src={attachmentUrl}
                                    alt="Receipt attachment"
                                    style={{ maxWidth: "100%", display: "block", borderRadius: 8 }}
                                />
                            ) : (
                                <Link href={attachmentUrl} target="_blank" rel="noopener noreferrer">
                                    View attachment
                                </Link>
                            )}
                        </Paper>
                    ) : null}

                    <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Approve expense</DialogTitle>
                        <DialogContent>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Approve: {expense.title} — {formatMWK(expense.amount)}
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    This will mark the expense as official school spend.
                                </Typography>
                            </Alert>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => {
                                    dispatch(approveExpense(id, { approvedById: currentUser._id }));
                                }}
                            >
                                Approve
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Reject expense</DialogTitle>
                        <DialogContent>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Rejecting this expense removes it from approved spend. Please provide a reason.
                            </Alert>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Reason for rejection"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                required
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={!rejectReason.trim()}
                                onClick={() => {
                                    dispatch(
                                        rejectExpense(id, {
                                            rejectedReason: rejectReason.trim(),
                                            approvedById: currentUser._id,
                                        })
                                    );
                                }}
                            >
                                Reject
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
                        <DialogTitle>Edit expense</DialogTitle>
                        <DialogContent>
                            {editForm && (
                                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Title"
                                            value={editForm.title}
                                            onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                label="Category"
                                                value={editForm.categoryId}
                                                onChange={(e) => setEditForm((f) => ({ ...f, categoryId: e.target.value }))}
                                            >
                                                {(Array.isArray(categoriesList) ? categoriesList : []).map((c) => (
                                                    <MenuItem key={c._id || c.id} value={c._id || c.id}>
                                                        <Box>
                                                            <Typography variant="body2">{c.name}</Typography>
                                                            {c.description ? (
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    {c.description}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="number"
                                            label="Amount"
                                            value={editForm.amount}
                                            onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">MWK</InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <InputLabel>Payment Method</InputLabel>
                                            <Select
                                                label="Payment Method"
                                                value={editForm.paymentMethod}
                                                onChange={(e) => setEditForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                                            >
                                                {PAYMENT_METHODS.map((m) => (
                                                    <MenuItem key={m} value={m}>
                                                        {m}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            required
                                            type="date"
                                            label="Expense Date"
                                            value={editForm.expenseDate}
                                            onChange={(e) => setEditForm((f) => ({ ...f, expenseDate: e.target.value }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Description"
                                            multiline
                                            rows={2}
                                            value={editForm.description}
                                            onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Payee"
                                            value={editForm.payee}
                                            onChange={(e) => setEditForm((f) => ({ ...f, payee: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Receipt Ref"
                                            value={editForm.receiptRef}
                                            onChange={(e) => setEditForm((f) => ({ ...f, receiptRef: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Reference Number"
                                            value={editForm.referenceNumber}
                                            onChange={(e) => setEditForm((f) => ({ ...f, referenceNumber: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Term</InputLabel>
                                            <Select
                                                label="Term"
                                                value={editForm.term}
                                                onChange={(e) => setEditForm((f) => ({ ...f, term: e.target.value }))}
                                            >
                                                {TERMS.map((t) => (
                                                    <MenuItem key={t} value={t}>
                                                        {t}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Academic Year</InputLabel>
                                            <Select
                                                label="Academic Year"
                                                value={editForm.academicYear}
                                                onChange={(e) => setEditForm((f) => ({ ...f, academicYear: e.target.value }))}
                                            >
                                                {ACADEMIC_YEARS.map((yr) => (
                                                    <MenuItem key={yr} value={yr}>
                                                        {yr}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Notes"
                                            multiline
                                            rows={2}
                                            value={editForm.notes}
                                            onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                            <Button variant="contained" onClick={handleSaveEdit} disabled={loading || !editForm}>
                                Save
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            )}
        </Container>
    );
};

export default ExpenseDetail;
